import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SvTestPannelService } from '../../../services/sv-test-pannel/sv-test-panel.service';
import { FormControl, Validators } from '@angular/forms';
import { DropdownComponent } from '../../../shared/dropdown/dropdown.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { SampletestService } from '../../../services/sampletest/sampletest.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { InputComponent } from '../../../shared/input/input.component';
import { ReactiveFormsModule } from '@angular/forms';
import { AppService } from '../../../services/application/app.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';

@Component({
  selector: 'app-sample-test-run-modal',
  standalone: true,
  imports: [
    MatDialogModule,
    DropdownComponent,
    ButtonsComponent,
    MatFormFieldModule,
    CommonModule,
    InputComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './sample-test-run-modal.component.html',
  styleUrl: './sample-test-run-modal.component.css',
})
export class SampleTestRunModalComponent {
  constructor(
    public dialogRef: MatDialogRef<SampleTestRunModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { title: string; sampleId: number },
    private svTestPannelService: SvTestPannelService,
    private sampleService: SampletestService,
    private appService: AppService,
    private messageModal: MessageModalService
  ) {}
  analysisNameControl = new FormControl('', [Validators.required, noWhitespaceValidator()]);
  analysisDescControl = new FormControl('', [noWhitespaceValidator()]);
  applicationControl = new FormControl<number | null>(null, [Validators.required]);
  // Disabled until an application is selected — see onApplicationChange().
  panelControl = new FormControl<number | null>({ value: null, disabled: true }, [Validators.required]);

  applicationDropdownOptions: Array<{ value: number; label: string }> = [];
  panelDropdownOptions: Array<{ value: number; label: string }> = [];
  isSubmitting: boolean = false;
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  cancelBtnLabel: string = $localize`Cancel`;
  doneBtnLabel: string = $localize`Run`;
  isErrorVisible: boolean = false;
  errorMessage: string = '';
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.loadApplications();
  }

  loadApplications() {
    this.sampleService
      .getAllApplicationsForOrg()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && Array.isArray(response.data)) {
            this.applicationDropdownOptions = response.data.map((app: any) => ({
              value: app.AppVersionID,
              label: `${app.Name} (v${app.Version})`,
            }));
          } else {
            this.isErrorVisible = true;
            this.errorMessage = 'Failed to load applications.';
          }
        },
        error: (err) => {
          console.error(err);
          this.isErrorVisible = true;
          this.errorMessage = 'An error occurred while loading applications.';
        },
      });
  }

  onApplicationChange(selectedAppId: number) {
    this.isErrorVisible = false;
    this.errorMessage = '';
    // When application changes, reset the panel selection and load new panels
    this.panelDropdownOptions = [];
    this.panelControl.reset();

    if (selectedAppId) {
      this.panelControl.enable();
      this.loadPanels(selectedAppId);
    } else {
      this.panelControl.disable();
    }
  }

  loadPanels(appVersionId: number) {
    this.svTestPannelService
      .getPanelsByAppVersionId(appVersionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            const readyPanels = response.data.filter((item: any) => item.Status === 'ready');
            this.panelDropdownOptions = readyPanels.map((e: any) => ({
              value: e.PanelID,
              label: e.Name,
            }));
            if (this.panelDropdownOptions.length === 0) {
              this.isErrorVisible = true;
              this.errorMessage = $localize`No 'ready' panels found for the selected application.`;
            }
          } else {
            this.isErrorVisible = false;
            this.errorMessage = '';
          }
        },
        error: (err) => {
          console.error(err);
          this.isErrorVisible = true;
          this.errorMessage = $localize`Error while fetching list of Panel.`;
        },
      });
  }

  closePopup() {
    this.dialogRef.close();
  }

  onSubmit() {
    if (
      !this.analysisNameControl.valid ||
      !this.applicationControl.valid ||
      !this.panelControl.valid ||
      this.isSubmitting
    ) {
      return;
    }
    this.isSubmitting = true;
    this.sampleService
      .runSample(
        this.analysisNameControl.value,
        this.analysisDescControl.value,
        this.panelControl.value!,
        this.data.sampleId,
        this.applicationControl.value!
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.closePopup();
            this.messageModal.openModal(
              $localize`Analysis Started`,
              $localize`The analysis has been initiated successfully.`
            );
          } else {
            this.isSubmitting = false;
            this.isErrorVisible = true;
            this.errorMessage = $localize`${response.message}`;
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.isErrorVisible = true;
          this.errorMessage = $localize`Error while running sample.`;
        },
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
